import React from 'react';
import { type LaunchDarklyFlags } from '@/common/launchDarkly';
import { useNavigatedFromForms } from '../../../hooks/use-navigated-from-forms';
import { SureYouWantToLeaveDialog } from './sure-you-want-to-leave-dialog';

// Shared across every instance of this hook: the builder mounts it more than once
// (footer + back buttons), each registering its own beforeunload listener. An
// intentional exit must stand down all of them, not just the instance whose
// button was clicked, or the browser still shows its native "Changes you made
// may not be saved" prompt on Save & Exit (AP-3101).
let navigationConfirmed = false;
let rearmTimer: ReturnType<typeof setTimeout> | undefined;

export function confirmNavigationAwayFromPage() {
  navigationConfirmed = true;
  // If the exit never actually unloads the page (no history entry to go back
  // to, or the page is later restored from the back/forward cache), re-arm the
  // warning rather than leaving it disarmed for the rest of the session. The
  // browser evaluates beforeunload as soon as a navigation starts, so a real
  // exit is always decided well within this window.
  clearTimeout(rearmTimer);
  rearmTimer = setTimeout(() => {
    navigationConfirmed = false;
  }, 5000);
}

export function useSureYouWantToLeaveDialog(args: { flags: LaunchDarklyFlags }) {
  const { flags } = args;

  const [showDialog, setShowDialog] = React.useState(false);
  const confirmActionRef = React.useRef<(() => void) | null>(null);

  const shouldUseFormsNavigation = useNavigatedFromForms();

  React.useEffect(() => {
    if (!shouldUseFormsNavigation) return;
    // Re-arm the warning if the builder is re-entered after a confirmed exit
    navigationConfirmed = false;

    function beforeUnloadCallBack(event: BeforeUnloadEvent) {
      if (!navigationConfirmed) {
        event.preventDefault();
        const confirmationMessage = 'Changes you have entered will not be saved.';
        return (event.returnValue = confirmationMessage);
      }
    }
    window.addEventListener('beforeunload', beforeUnloadCallBack);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadCallBack);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  function confirm(buttonFunction?: () => void) {
    if (shouldUseFormsNavigation) {
      setShowDialog(true);
      // Can't provide a function directly to React state setter because React will treat that as the updater function
      confirmActionRef.current = buttonFunction ?? null;
    } else if (buttonFunction) {
      buttonFunction();
    }
  }

  function onNavigateAwayFromPage() {
    confirmNavigationAwayFromPage();
  }

  function renderDialog() {
    if (flags?.['hide-build-ui-elements'] || !showDialog) return null;
    return (
      <SureYouWantToLeaveDialog
        close={() => {
          setShowDialog(false);
          confirmActionRef.current = null;
        }}
        confirmBack={() => {
          if (confirmActionRef.current) {
            confirmActionRef.current();
          } else {
            window.history.back();
          }
        }}
      />
    );
  }

  return {
    onNavigateAwayFromPage,
    renderDialog,
    showModal: confirm,
  };
}
